import React, { FC, useCallback } from 'react';
import { Col, Row, Form, Upload } from 'antd';
import { CloudUploadOutlined } from '@ant-design/icons';
import { useTranslate } from '@refinedev/core';
import dayjs from 'dayjs';
import type { UploadFile } from 'antd/es/upload/interface';
import { FormItem } from 'components/ui';
import styles from './steps.module.scss';

interface Step4DocumentsProps {
    fileList: UploadFile[];
    onFileChange: (files: UploadFile[]) => void;
    accept?: string;
    requireFiles?: boolean;
}

export const Step4Documents: FC<Step4DocumentsProps> = ({
    fileList,
    onFileChange,
    accept = '.pdf,.doc,.docx',
    requireFiles = false,
}) => {
    const t = useTranslate();
    const form = Form.useFormInstance();

    const handleFileChange = useCallback(
        (info: any) => {
            const incomingFiles: UploadFile[] = info.fileList || [];
            const hasRemoval = info.file?.status === 'removed';

            if (hasRemoval) {
                const remaining = incomingFiles.filter(
                    file => file.status !== 'removed'
                );
                onFileChange(remaining);
                form?.setFieldsValue({ files: remaining });
                return;
            }

            const freshUploads = incomingFiles.filter(file =>
                Boolean((file as any).originFileObj)
            );

            const nextFiles = freshUploads.length
                ? freshUploads
                : incomingFiles;

            onFileChange(nextFiles);
            form?.setFieldsValue({ files: nextFiles });
        },
        [form, onFileChange]
    );

    const getValueFromEvent = (e: any): UploadFile[] => {
        if (Array.isArray(e)) {
            return e;
        }
        return e?.fileList || [];
    };

    const validateFiles = (_: any, value: UploadFile[]) => {
        if (!value || value.length === 0) {
            if (requireFiles) {
                return Promise.reject(
                    new Error(
                        t(
                            'preliminary-applications.message.please_upload_your_files',
                            'Будь ласка, завантажте файли'
                        )
                    )
                );
            }

            // Files are optional, so no error if empty
            return Promise.resolve();
        }

        const allowedTypes = ['application/pdf'];
        const maxSizeMB = 10;

        for (const file of value) {
            if ((file as any).originFileObj) {
                const { type, size, name } = (file as any)
                    .originFileObj as File;

                if (!allowedTypes.includes(type)) {
                    return Promise.reject(
                        new Error(
                            `${name} ${t(
                                'preliminary-applications.message.has_an_incorrect_file_type',
                                'has an incorrect file type'
                            )}`
                        )
                    );
                }

                if (size / 1024 / 1024 >= maxSizeMB) {
                    return Promise.reject(
                        new Error(
                            `${name} ${t(
                                'preliminary-applications.message.exceeds_maxsize',
                                { maxSize: maxSizeMB },
                                `exceeds max size of ${maxSizeMB}MB`
                            )}`
                        )
                    );
                }
            }
        }

        return Promise.resolve();
    };

    const handleRemoveFile = (file: UploadFile) => {
        const newFileList = fileList.filter(f => f.uid !== file.uid);
        onFileChange(newFileList);
        form?.setFieldsValue({ files: newFileList });
    };

    const formatFileSize = (bytes: number = 0): string => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i)) + sizes[i];
    };

    const formatFileDate = (file: UploadFile): string => {
        const date = (file as any).lastModifiedDate || new Date();
        return dayjs(date).format('DD MMM, YYYY  HH:mm');
    };

    return (
        <div className={styles.stepContainer}>
            <h2 className={styles.stepTitle}>
                {t(
                    'preliminary-applications.wizard.step4.title',
                    'Прикріпити документи'
                )}
            </h2>

            <Row gutter={[16, 20]}>
                <Col xs={24}>
                    <FormItem
                        name="files"
                        valuePropName="fileList"
                        getValueFromEvent={getValueFromEvent}
                        rules={
                            requireFiles || fileList?.length
                                ? [{ validator: validateFiles }]
                                : []
                        }
                    >
                        <Upload.Dragger
                            fileList={[]}
                            onChange={handleFileChange}
                            beforeUpload={() => false}
                            accept={accept}
                            multiple
                            maxCount={20}
                            showUploadList={false}
                            className={styles.dropzoneArea}
                        >
                            <div className={styles.dropContent}>
                                <div className={styles.statusIdle}>
                                    <CloudUploadOutlined />
                                </div>
                                <span className={styles.dropTitle}>
                                    <span className={styles.dropLink}>
                                        {t(
                                            'preliminary-applications.upload_click',
                                            'Натисніть для завантаження файлу зі свого носієві'
                                        )}
                                    </span>{' '}
                                    {t(
                                        'preliminary-applications.upload_drag',
                                        'або перетягніть його сюди'
                                    )}
                                </span>
                                <span className={styles.dropSubtitle}>
                                    {t(
                                        'preliminary-applications.supported_formats',
                                        'Підтримуються формати: PDF, docs'
                                    )}
                                </span>
                            </div>
                        </Upload.Dragger>
                    </FormItem>

                    {fileList.length > 0 && (
                        <>
                            <h3 className={styles.uploadedFilesTitle}>
                                {t(
                                    'preliminary-applications.uploaded_files',
                                    'Завантажені файли'
                                )}
                            </h3>
                            <ul className={styles.filesList}>
                                {fileList.map(file => (
                                    <li
                                        key={file.uid}
                                        className={styles.fileItem}
                                    >
                                        <span
                                            className={styles.fileItemIcon}
                                            aria-hidden
                                        />
                                        <div className={styles.fileInfo}>
                                            <div className={styles.fileName}>
                                                {file.name}
                                            </div>
                                            <div className={styles.fileMeta}>
                                                {formatFileDate(file)} •{' '}
                                                {formatFileSize(
                                                    (file as any).size
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.fileRemove}
                                            onClick={() =>
                                                handleRemoveFile(file)
                                            }
                                            aria-label={t(
                                                'preliminary-applications.remove_file',
                                                'Remove file'
                                            )}
                                        >
                                            ×
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </Col>
            </Row>
        </div>
    );
};
